import React, {
    createContext,
    useReducer,
    useEffect,
    useMemo,
    useCallback,
    useState,
    useRef,
} from "react";

import useUtmData from "../../hooks/useUtmData";
import { useCurrentPath } from "../../hooks/useCurrentPath";
import { useAppConfig } from "../AppConfig/AppConfigContext";
import { useUrlContext } from "../urlContext";

import { userDataReducer, defaultInitialState, UserAction } from "./reducer";
import { USER_ACTIONS, AGE_RANGE_MAPPING } from "./constants";
import type { UserState, HolidayPromoFlow, HolidayPromoState } from "./types";

import {
    fetchInitialCID,
    updateCIDWithEmail,
    getEmailCheck,
    getEmailChanged,
    getEmailChangedOldFlow,
    postUserLinkEmail,
    checkUserLinkedEmail,
    getGoogleClientID,
} from "./utils/api";

import {
    parseUParam,
    parsePayload,
    readFromLocalStorage,
    syncToStorage,
    mergeDataSources,
} from "./utils/storage";

import { useConsoleLog } from "../../hooks/consoleLog";
import { getCurrentPageUrl } from "../../utils/pageUrl";
import { getTransferredAmplitudeDeviceId } from "../../analytics/services/sessionTransfer";

import Loader from "../../templates/Loader/Loader";

interface UserDataContextValue {
    state: UserState;
    dispatch: React.Dispatch<UserAction>;

    setStateUserEmail: (email: string) => Promise<void>;
    emailChangeTrigerOldFlow: (emailNew: string) => Promise<void>;
    setStateQuizUserEmail: (email: string, status?: 'check' | 'change', quizVersion?: string) => Promise<void>;
    setStateQuizUserCID: (cid: string) => void;
    initializeNewUserCID: () => Promise<void>;
    setStateQuizUserGoal: (goal: string) => void;
    setStateUserName: (name: string) => void;
    setStateUserAgeRange: (ageRange: string) => void;
    setStateUserGender: (gender: string) => void;

    setUserOrderID: (orderId: string) => void;
    setPaymentBackPath: (path: string) => void;
    setHolidayPromoState: (flow: HolidayPromoFlow, patch: Partial<HolidayPromoState>) => void;

    initUserEmailLinking: (provider: string, profile: { email?: string }) => void;

    trackUserAnalytics: () => void;
    trigerUserAnalytics: boolean;
    setTrigerUserAnalytics: (value: boolean) => void;

    userStateIsLoading: boolean;
    linkLoadingStatus: boolean;
}

export const UserDataContext = createContext<UserDataContextValue | undefined>(undefined);

interface UserDataProviderProps {
    children: React.ReactNode;
}

export const UserDataProvider: React.FC<UserDataProviderProps> = ({ children }) => {
    const { encodeToUrl, decodeFromUrl }: any = useUtmData('u');
    const { newH2path, newT2path, newSpath, iqTestPath, holidayPromoPath, holidayPromo3Path, holidayPromo23dPath } = useUrlContext();
    const { appData, isConfigReady } = useAppConfig();
    const currentPath = useCurrentPath();

    const [state, dispatch] = useReducer(userDataReducer, defaultInitialState);

    const [isInitialized, setIsInitialized] = useState(false);
    const [userStateIsLoading, setUserStateIsLoading] = useState(false);
    const [linkLoadingStatus, setLinkLoadingStatus] = useState(false);

    const [trigerUserAnalytics, setTrigerUserAnalytics] = useState(false);
    const [hasCheckedEmail, setHasCheckedEmail] = useState(false);

    const [startEmailLink, setStartEmailLink] = useState(false);

    const isInitializing = useRef(false);

    const logger = useConsoleLog();

    const flowEndpoint: string = newH2path
        ? "new-h2"
        : newT2path
            ? "new-t2"
            : newSpath
                ? "new-s"
                : iqTestPath
                    ? "iq-test"
                    : holidayPromoPath
                        ? "hp"
                        : holidayPromo3Path
                            ? "hp-3"
                            : holidayPromo23dPath
                                ? "hp-23d"
                                : "";

    useEffect(() => {
        const initializeUserData = async () => {
            if (isInitializing.current || isInitialized) {
                logger.specialInfo('[Init] Already initialized, skipping...');
                return;
            }

            if (!isConfigReady || !appData?.REACT_APP_API_ENDPOINT) {
                logger.specialInfo('[Init] Waiting for config...');
                return;
            }

            isInitializing.current = true;
            logger.specialInfo('[Init] === Starting initialization ===');

            try {
                logger.specialInfo('[Init] Step 1: Gathering data from sources...');

                const uParamData = parseUParam(decodeFromUrl);
                const payloadData = parsePayload();
                const localStorageData = readFromLocalStorage();

                logger.specialInfo('[Init] Data sources:', {
                    uParam: uParamData,
                    payload: payloadData,
                    localStorage: localStorageData,
                });

                const mergedData = mergeDataSources(uParamData, payloadData, localStorageData);
                logger.specialInfo('[Init] Merged data:', mergedData);

                let finalCID = mergedData.cid || null;

                if (!finalCID) {
                    logger.specialInfo('[Init] No CID found, fetching from API...');
                    finalCID = await fetchInitialCID(appData.REACT_APP_API_ENDPOINT);
                    logger.specialInfo('[Init] Fetched CID from API:', finalCID);
                } else {
                    logger.specialInfo('[Init] Using existing CID:', finalCID);
                }

                if (mergedData.email && finalCID) {
                    logger.specialInfo('[Init] Validating email with CID...');

                    if (flowEndpoint) {
                        logger.specialInfo('[Init] Using quiz flow for email validation');
                        const validatedCID = await getEmailCheck(
                            appData.REACT_APP_API_ENDPOINT,
                            flowEndpoint,
                            finalCID,
                            mergedData.email
                        );
                        finalCID = validatedCID;
                        logger.specialInfo('[Init] Quiz validated CID:', finalCID);
                    } else {
                        logger.specialInfo('[Init] Using standard flow for email validation');
                        const validatedCID = await updateCIDWithEmail(
                            appData.REACT_APP_API_ENDPOINT,
                            finalCID,
                            mergedData.email
                        );
                        finalCID = validatedCID;
                        logger.specialInfo('[Init] Standard validated CID:', finalCID);
                    }
                }

                const finalState: Partial<UserState> = {
                    ...mergedData,
                    cid: finalCID,
                    device_id: getTransferredAmplitudeDeviceId() ?? mergedData.device_id ?? null,
                };

                logger.specialInfo('[Init] Final state to set:', finalState);

                dispatch({ type: USER_ACTIONS.SET_MULTIPLE, payload: finalState });

                const stateToSync = { ...defaultInitialState, ...finalState };
                syncToStorage(stateToSync, encodeToUrl);

                getGoogleClientID()
                    .then((gcid) => {
                        logger.specialInfo('[Init] Google Client ID received:', gcid);
                        dispatch({ type: USER_ACTIONS.SET_GCID, payload: gcid });
                    })
                    .catch((error) => {
                        logger.error('[Init] Failed to get Google Client ID:', error);
                    });

                logger.specialInfo('[Init] === Initialization completed successfully ===');
                setIsInitialized(true);
            } catch (error) {
                logger.error('[Init] === Initialization FAILED ===', error);
                // infinity loader or show error popup like on payment page err
            } finally {
                isInitializing.current = false;
            }
        };

        initializeUserData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConfigReady, appData, flowEndpoint]);

    useEffect(() => {
        if (!isInitialized) return;

        logger.specialInfo('[Sync] State changed, syncing to storage...');
        syncToStorage(state, encodeToUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state, isInitialized, encodeToUrl]);

    const setStateUserName = useCallback((name: string) => {
        if (!name) {
            logger.error('[UserData] User name is missing');
            return;
        }

        logger.specialInfo('[UserData] Setting user name:', name);
        dispatch({ type: USER_ACTIONS.SET_NAME, payload: name });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setStateUserEmail = useCallback(
        async (email: string) => {
            logger.info('[UserData] Setting user email:', email);

            if (!state.cid) {
                logger.error('[UserData] Cannot set email: CID is not available');
                return;
            }

            dispatch({ type: USER_ACTIONS.SET_EMAIL, payload: email });

            if (!flowEndpoint && appData?.REACT_APP_API_ENDPOINT) {
                try {
                    setUserStateIsLoading(true);
                    const updatedCID = await updateCIDWithEmail(
                        appData.REACT_APP_API_ENDPOINT,
                        state.cid,
                        email
                    );

                    if (updatedCID !== state.cid) {
                        dispatch({ type: USER_ACTIONS.SET_CID, payload: updatedCID });
                        logger.info('[UserData] CID updated after email validation:', updatedCID);
                    }
                } catch (error) {
                    logger.error('[UserData] Failed to update CID with email:', error);
                } finally {
                    setUserStateIsLoading(false);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state.cid, flowEndpoint, appData]
    );

    const emailChangeTrigerOldFlow = useCallback(
        async (emailNew: string): Promise<void> => {
            const apiEndpoint = appData?.REACT_APP_API_ENDPOINT;
            const emailOld = state.email;

            if (!apiEndpoint || !state.cid || !emailOld) {
                logger.error('[UserData] Missing API endpoint, CID, or old email');
                return;
            }

            try {
                await getEmailChangedOldFlow(
                    apiEndpoint,
                    state.cid,
                    emailOld,
                    emailNew,
                    getCurrentPageUrl()
                );
                logger.info('[UserData] Old flow email change notification sent');
            } catch (error) {
                logger.error('[UserData] Failed to notify old flow about email change:', error);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [appData, state.cid, state.email]
    );

    const setStateQuizUserEmail = useCallback(
        async (email: string, status?: 'check' | 'change', quizVersion?: string) => {
            logger.info('[UserData] Setting quiz user email:', { email, status, quizVersion });

            if (!appData?.REACT_APP_API_ENDPOINT || !state.cid) {
                logger.error('[UserData] Missing API endpoint or CID');
                return;
            }

            try {
                if (status === 'check') {
                    const updatedCID = await getEmailCheck(
                        appData.REACT_APP_API_ENDPOINT,
                        flowEndpoint,
                        state.cid,
                        email
                    );

                    dispatch({ type: USER_ACTIONS.SET_EMAIL, payload: email });
                    dispatch({ type: USER_ACTIONS.SET_CID, payload: updatedCID });
                    logger.info('[UserData] Quiz email checked, CID:', updatedCID);

                } else if (status === 'change') {
                    if (!state.email || !quizVersion) {
                        logger.error('[UserData] Missing old email or quiz version');
                        return;
                    }

                    const updatedCID = await getEmailChanged(
                        appData.REACT_APP_API_ENDPOINT,
                        flowEndpoint,
                        state.cid,
                        state.email,
                        email,
                        quizVersion
                    );

                    dispatch({ type: USER_ACTIONS.SET_EMAIL, payload: email });
                    dispatch({ type: USER_ACTIONS.SET_CID, payload: updatedCID });
                    logger.info('[UserData] Quiz email changed, CID:', updatedCID);

                } else {
                    dispatch({ type: USER_ACTIONS.SET_EMAIL, payload: email });
                }
            } catch (error) {
                logger.error('[UserData] Error in setStateQuizUserEmail:', error);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [state.cid, state.email, appData, flowEndpoint]
    );

    const setStateQuizUserCID = useCallback((cid: string) => {
        logger.info('[UserData] Setting quiz user CID:', cid);
        dispatch({ type: USER_ACTIONS.SET_CID, payload: cid });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initializeNewUserCID = useCallback(async () => {
        if (!appData?.REACT_APP_API_ENDPOINT) {
            logger.error('[UserData] Cannot initialize new CID: API endpoint is missing');
            return;
        }

        try {
            logger.info('[UserData] Initializing new CID');
            const newCID = await fetchInitialCID(appData.REACT_APP_API_ENDPOINT);
            dispatch({ type: USER_ACTIONS.SET_CID, payload: newCID });
            logger.info('[UserData] New CID initialized:', newCID);
        } catch (error) {
            logger.error('[UserData] Failed to initialize new CID:', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appData]);

    // const setStateQuizUserGoal = useCallback((goal: string) => {
    //     logger.info('[UserData] Setting user goal:', goal);
    //     dispatch({ type: USER_ACTIONS.SET_USER_GOAL, payload: goal });
    // }, []);

    const setStateQuizUserGoal = useCallback((goal: string) => {

        const cleanGoal = goal
            .replace(/[\u{1F600}-\u{1F64F}]/gu, '')
            .replace(/[\u{1F300}-\u{1F5FF}]/gu, '')
            .replace(/[\u{1F680}-\u{1F6FF}]/gu, '')
            .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '')
            .replace(/[\u{2600}-\u{26FF}]/gu, '')
            .replace(/[\u{2700}-\u{27BF}]/gu, '')
            .replace(/[\u{FE00}-\u{FE0F}]/gu, '')
            .replace(/[\u{1F900}-\u{1F9FF}]/gu, '')
            .replace(/[\u{200D}]/gu, '')
            .trim();

        logger.info('[UserData] Setting user goal:', cleanGoal);
        dispatch({ type: USER_ACTIONS.SET_USER_GOAL, payload: cleanGoal });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setStateUserAgeRange = useCallback((ageRange: string) => {
        if (!ageRange) {
            logger.error('[UserData] User age range is missing');
            return;
        }

        const mappedAgeRange = AGE_RANGE_MAPPING[ageRange] || ageRange;
        logger.info('[UserData] Setting user age range:', { original: ageRange, mapped: mappedAgeRange });
        dispatch({ type: USER_ACTIONS.SET_USER_AGE_RANGE, payload: mappedAgeRange });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setStateUserGender = useCallback((gender: string) => {
        if (!gender) {
            logger.error('[UserData] User gender is missing');
            return;
        }

        const genderToStore = gender === "undisclosed" ? "male" : gender;
        logger.info('[UserData] Setting user gender:', { original: gender, stored: genderToStore });
        dispatch({ type: USER_ACTIONS.SET_USER_GENDER, payload: genderToStore });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setUserOrderID = useCallback((orderId: string) => {
        logger.info('[UserData] Setting user order ID:', orderId);
        dispatch({ type: USER_ACTIONS.SET_USER_ORDER_ID, payload: orderId });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setPaymentBackPath = useCallback((path: string) => {
        logger.info('[UserData] Setting payment back path:', path);
        dispatch({ type: USER_ACTIONS.SET_PAYMENT_BACK_PATH, payload: path });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setHolidayPromoState = useCallback((flow: HolidayPromoFlow, patch: Partial<HolidayPromoState>) => {
        dispatch({ type: USER_ACTIONS.SET_HOLIDAY_PROMO_STATE, payload: { flow, patch } });
    }, []);

    const initUserEmailLinking = useCallback(
        (provider: string, profile: { email?: string }) => {
            logger.info('[EmailLink] Initializing email linking:', { provider, profile });

            if (!provider || !profile || !profile.email) {
                logger.error('[EmailLink] Missing provider or profile email');
                dispatch({ type: USER_ACTIONS.SET_SOCIAL_PROFILE_EMAIL, payload: '' });
                return;
            }

            setLinkLoadingStatus(true);
            dispatch({ type: USER_ACTIONS.SET_SOCIAL_PROVIDER, payload: provider });
            dispatch({ type: USER_ACTIONS.SET_SOCIAL_PROFILE_EMAIL, payload: profile.email });
            setStartEmailLink(true);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        const linkEmail = async () => {
            if (
                !startEmailLink ||
                !state.social_provider ||
                !state.social_profile_email ||
                !state.user_order_id ||
                !appData?.REACT_APP_API_ENDPOINT
            ) {
                return;
            }

            try {
                logger.info('[EmailLink] Starting email link process...');

                const success = await postUserLinkEmail(
                    appData.REACT_APP_API_ENDPOINT,
                    state.user_order_id,
                    state.social_provider,
                    state.social_profile_email,
                    getCurrentPageUrl()
                );

                if (success) {
                    dispatch({ type: USER_ACTIONS.SET_LINKED_EMAIL, payload: true });
                }
            } catch (error) {
                logger.error('[EmailLink] Failed to link email:', error);
            } finally {
                setLinkLoadingStatus(false);
                setStartEmailLink(false);
            }
        };

        linkEmail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startEmailLink, state.social_provider, state.social_profile_email, state.user_order_id, appData]);

    const isCheckingEmail = useRef(false);

    useEffect(() => {
        const checkEmailLink = async () => {
            const thankyouPagePath = /^\/($|\?)|(\/thankyou)(\/|$|\?|#)/.test(currentPath);

            if (
                thankyouPagePath &&
                !newH2path &&
                !newSpath &&
                !hasCheckedEmail &&
                !isCheckingEmail.current &&
                state.user_order_id &&
                appData?.REACT_APP_API_ENDPOINT
            ) {
                isCheckingEmail.current = true;
                logger.info('[EmailLink] Checking email link status on thankyou page...');

                try {
                    const linked = await checkUserLinkedEmail(
                        appData.REACT_APP_API_ENDPOINT,
                        state.user_order_id
                    );
                    dispatch({ type: USER_ACTIONS.SET_LINKED_EMAIL, payload: linked });
                    setHasCheckedEmail(true);
                } catch (error) {
                    logger.error('[EmailLink] Failed to check email link status:', error);
                } finally {
                    isCheckingEmail.current = false;
                }
            }
        };

        checkEmailLink();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPath, state.user_order_id, hasCheckedEmail, appData, newH2path, newSpath]);

    const trackUserAnalytics = useCallback(() => {
        const { cid, email, name, analytics_tracked } = state;

        if (!cid || !email || !name) {
            return;
        }

        const valuesChanged =
            cid !== analytics_tracked.lastTrackedValues.cid ||
            email !== analytics_tracked.lastTrackedValues.email ||
            name !== analytics_tracked.lastTrackedValues.name;

        if (!analytics_tracked.eventsTracked || valuesChanged) {
            try {
                setTrigerUserAnalytics(true);

                const newTrackedState = {
                    eventsTracked: true,
                    lastTrackedValues: { cid, email, name },
                };

                dispatch({
                    type: USER_ACTIONS.SET_ANALYTICS_TRACKED,
                    payload: newTrackedState,
                });

                logger.info('[Analytics] Events tracked successfully:', newTrackedState);
            } catch (error) {
                logger.error('[Analytics] Error tracking analytics events:', error);
            }
        } else {
            logger.info('[Analytics] Events already tracked for current user data');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    useEffect(() => {
        trackUserAnalytics();
    }, [state.cid, state.email, state.name, trackUserAnalytics]);

    const contextValue = useMemo<UserDataContextValue>(
        () => ({
            state,
            dispatch,
            setStateUserEmail,
            emailChangeTrigerOldFlow,
            setStateQuizUserEmail,
            setStateQuizUserCID,
            initializeNewUserCID,
            setStateQuizUserGoal,
            setStateUserName,
            setStateUserAgeRange,
            setStateUserGender,
            setUserOrderID,
            setPaymentBackPath,
            setHolidayPromoState,
            initUserEmailLinking,
            trackUserAnalytics,
            trigerUserAnalytics,
            setTrigerUserAnalytics,
            userStateIsLoading,
            linkLoadingStatus,
        }),
        [
            state,
            setStateUserEmail,
            emailChangeTrigerOldFlow,
            setStateQuizUserEmail,
            setStateQuizUserCID,
            initializeNewUserCID,
            setStateQuizUserGoal,
            setStateUserName,
            setStateUserAgeRange,
            setStateUserGender,
            setUserOrderID,
            setPaymentBackPath,
            setHolidayPromoState,
            initUserEmailLinking,
            trackUserAnalytics,
            trigerUserAnalytics,
            userStateIsLoading,
            linkLoadingStatus,
        ]
    );

    if (!isConfigReady || !isInitialized) {
        return <Loader />;
    }

    return (
        <UserDataContext.Provider value={contextValue}>
            {userStateIsLoading && <Loader />}
            {children}
        </UserDataContext.Provider>
    );
};

export { useUserData } from './hooks/useUserData';
